"use client";

import { useEffect, useRef } from "react";
import type { MenuGroup } from "@/features/site-content/menus";
import { telHref } from "@/lib/utils/contact";
import { IconMenu, IconPhone, IconUser } from "@/components/ui/icons";
import { Sheet } from "@/components/ui/sheet";
import { buttonClass } from "@/components/ui/button";
import { MenuAnchor } from "./menu-link";
import { useCurrentPath } from "./use-current-path";

type Labels = { menu: string; open: string; close: string; mobileAria: string; agencyLogin: string; newTab: string };

// Sağdan açılan çekmece (Sheet → yerel <dialog> showModal(): odak tuzağı, Esc, arka plan erişilemez). Bağlantılar
// masaüstü menüyle aynı listeden (Site Yönetimi → Menüler).
export function MobileMenu({ nav, phone, agencyPanelUrl, labels }: { nav: MenuGroup[]; phone: string; agencyPanelUrl: string; labels: Labels }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const pathname = useCurrentPath();

  useEffect(() => {
    dialogRef.current?.close();
  }, [pathname]);

  return (
    <div className="lg:hidden">
      <button
        type="button"
        onClick={() => dialogRef.current?.showModal()}
        className="inline-flex h-11 w-11 items-center justify-center rounded-md text-ink hover:bg-primary-soft"
        aria-haspopup="dialog"
      >
        <IconMenu size={24} />
        <span className="sr-only">{labels.open}</span>
      </button>
      <Sheet
        dialogRef={dialogRef}
        side="right"
        titleId="mobile-menu-title"
        title={labels.menu}
        label={labels.menu}
        closeLabel={labels.close}
        footer={
          <div className="grid gap-2">
            <a href={agencyPanelUrl} target="_blank" rel="noopener noreferrer" className={buttonClass("primary", "md", "w-full")}>
              <IconUser size={18} /> {labels.agencyLogin}
            </a>
            <a href={telHref(phone)} className={buttonClass("secondary", "md", "w-full")}>
              <IconPhone size={18} /> {phone}
            </a>
          </div>
        }
      >
        <nav aria-label={labels.mobileAria} className="px-2 py-3">
          <ul className="grid gap-1">
            {nav.map((group) => (
              <li key={group.key}>
                <MenuAnchor
                  link={group}
                  newTabLabel={labels.newTab}
                  current={!group.external && pathname === group.href}
                  className="flex min-h-12 items-center rounded-md px-3 text-base font-semibold text-ink hover:bg-primary-soft aria-[current=page]:bg-primary-soft"
                />
                {group.children && (
                  <ul className="mb-2 ml-3 grid border-l-2 border-line pl-2">
                    {group.children.map((child) => (
                      <li key={child.key}>
                        <MenuAnchor
                          link={child}
                          newTabLabel={labels.newTab}
                          current={!child.external && pathname === child.href}
                          className="flex min-h-11 items-center rounded-md px-3 text-[0.95rem] text-ink-muted hover:bg-primary-soft hover:text-ink aria-[current=page]:font-semibold aria-[current=page]:text-ink"
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </nav>
      </Sheet>
    </div>
  );
}
